import React, { useState } from "react";
import classNames from "classnames";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronDown, faChevronUp } from "@fortawesome/pro-solid-svg-icons";
import useOnclickOutside from "hooks/useOnclickOutside/useOnclickOutside";
import Sheet from "components/sheet/Sheet";
import Portal from "elements/Portal";
import { WEBSITE_BUILDER_PORTAL } from "pages/secure/facility/websiteBuilder/editor/WebsiteBuilderEditor";
import {
  TWebsiteBuilderHeaderPreset,
  WEBSITE_BUILDER_HEADER_PRESETS,
  websiteBuilderHeaderPresetIncludesCta,
} from "pages/secure/facility/websiteBuilder/editor/nodes/section/websiteBuilderHeaderPreset";

interface IHeaderPresetPickerProps {
  onApply: (preset: TWebsiteBuilderHeaderPreset) => void;
}

function HeaderPresetPreviewLinks() {
  return (
    <span className="header-preset-preview-links">
      <i />
      <i />
      <i />
    </span>
  );
}

function HeaderPresetPreview(props: { preset: TWebsiteBuilderHeaderPreset }) {
  const { preset } = props;

  const { t } = useTranslation();

  return (
    <div className={classNames("website-builder-header-preset-preview", `preview-${preset}`)}>
      <span className="header-preset-preview-logo">{t("LOGO")}</span>
      <HeaderPresetPreviewLinks />
      {websiteBuilderHeaderPresetIncludesCta(preset) && <span className="header-preset-preview-cta" />}
    </div>
  );
}

export function HeaderPresetPicker(props: IHeaderPresetPickerProps) {
  const { onApply } = props;

  const { t } = useTranslation();

  const [open, setOpen] = useState(false);
  const [pendingPreset, setPendingPreset] = useState<TWebsiteBuilderHeaderPreset>(null);

  const dropdownRef = useOnclickOutside(() => {
    setOpen(false);
  });

  function handleSelect(preset: TWebsiteBuilderHeaderPreset) {
    setOpen(false);
    setPendingPreset(preset);
  }

  function handleApply() {
    onApply(pendingPreset);
    setPendingPreset(null);
  }

  return (
    <div
      className={classNames("website-builder-header-preset-picker", { "picker-open": open })}
      ref={dropdownRef}
    >
      <div className="header-preset-picker-label">{t("Header presets")}</div>
      <button
        type="button"
        className={classNames("header-preset-picker-trigger", { "trigger-open": open })}
        onClick={() => setOpen(prev => !prev)}
      >
        <span className="header-preset-picker-placeholder">{t("Apply a preset")}</span>
        <FontAwesomeIcon
          className="header-preset-picker-chevron"
          icon={open ? faChevronUp : faChevronDown}
        />
      </button>
      {open && (
        <div className="header-preset-picker-menu">
          {WEBSITE_BUILDER_HEADER_PRESETS.map(preset => (
            <button
              key={preset}
              type="button"
              className="header-preset-picker-option"
              onClick={() => handleSelect(preset)}
            >
              <HeaderPresetPreview preset={preset} />
            </button>
          ))}
        </div>
      )}
      {pendingPreset != null && (
        <Portal isMounted parentId={WEBSITE_BUILDER_PORTAL}>
          <Sheet
            open
            stacked
            closable
            size="small"
            title={t("Apply header preset?")}
            okText={t("Apply preset")}
            onOk={handleApply}
            onCancel={() => setPendingPreset(null)}
          >
            <div className="website-builder-header-preset-sheet-content">
              <HeaderPresetPreview preset={pendingPreset} />
              <p>
                {websiteBuilderHeaderPresetIncludesCta(pendingPreset)
                  ? t("This rearranges your header into the selected layout. Your logo, menu and button are kept. Other header elements are removed.")
                  : t("This rearranges your header into the selected layout. Your logo and menu are kept. Other header elements are removed.")}
              </p>
            </div>
          </Sheet>
        </Portal>
      )}
    </div>
  );
}
